import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Upload,
  FileText,
  Mail,
  Play,
  Download,
  Save,
  Phone,
  Globe,
  MapPin,
  Building2,
  Users,
  ChevronDown,
  ChevronUp,
  Linkedin,
  ExternalLink,
  Copy,
  CheckCircle2,
  XCircle,
  Shield
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailLookupPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [emailText, setEmailText] = useState('');
  const [parsedEmails, setParsedEmails] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDomain, setExpandedDomain] = useState(null);
  const pollRef = useRef(null);

  const parseEmails = (text) => {
    const pattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const found = [...new Set((text.match(pattern) || []).map(e => e.toLowerCase()))];
    return found;
  };

  const handleTextChange = (text) => {
    setEmailText(text);
    setParsedEmails(parseEmails(text));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/email-lookup/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const emails = res.data.emails || [];
      const combined = [...new Set([...parsedEmails, ...emails])];
      setParsedEmails(combined);
      setEmailText(prev => prev + (prev ? '\n' : '') + emails.join('\n'));
      toast.success(`${emails.length} email estratte da ${file.name}`);
    } catch {
      toast.error('Errore nel caricamento del file');
    }
    e.target.value = '';
  };

  const startLookup = async () => {
    if (parsedEmails.length === 0) {
      toast.error('Nessuna email da analizzare');
      return;
    }
    setLoading(true);
    setJob(null);
    try {
      const res = await axios.post(`${API_URL}/email-lookup/start`, { emails: parsedEmails });
      setJobId(res.data.job_id);
      toast.success(`Analisi avviata per ${parsedEmails.length} email`);
    } catch {
      toast.error('Errore nell\'avvio dell\'analisi');
      setLoading(false);
    }
  };

  const pollStatus = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/scraper/status/${jobId}`);
      setJob(res.data);
      if (res.data.status === 'completed' || res.data.status === 'failed') {
        setLoading(false);
        clearInterval(pollRef.current);
        if (res.data.status === 'completed') {
          toast.success(`Completato! ${res.data.results?.length || 0} domini analizzati`);
        }
      }
    } catch { /* ignore */ }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      pollRef.current = setInterval(pollStatus, 3000);
      pollStatus();
      return () => clearInterval(pollRef.current);
    }
  }, [jobId, pollStatus]);

  const exportCSV = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API_URL}/email-lookup/results/${jobId}/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `email_lookup_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success('CSV scaricato!');
    } catch {
      toast.error('Errore export CSV');
    }
  };

  const saveAsLeads = async () => {
    if (!jobId) return;
    try {
      const res = await axios.post(`${API_URL}/scraper/save-to-leads/${jobId}`);
      toast.success(res.data.message);
    } catch {
      toast.error('Errore nel salvataggio');
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato!');
  };

  const results = job?.results || [];
  const domains = [...new Set(parsedEmails.map(e => e.split('@')[1]))];

  const statusLabel = {
    pending: 'In attesa...',
    running: 'Analisi in corso...',
    completed: 'Completato',
    failed: 'Errore'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <input ref={fileInputRef} type="file" accept=".csv,.txt,.xlsx" className="hidden" onChange={handleFileUpload} />

      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-600 text-white flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Email Lookup</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Input Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Inserisci Email Aziendali
              </CardTitle>
              <CardDescription>
                Inserisci email aziendali per recuperare dati dell'azienda, referenti e contatti per la tua campagna
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">

              {/* Textarea */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Email (una per riga o separate da virgola)</Label>
                <textarea
                  value={emailText}
                  onChange={(e) => handleTextChange(e.target.value)}
                  placeholder={"info@azienda1.it\ncontatto@azienda2.com\nsales@azienda3.it"}
                  className="w-full h-36 rounded-lg border border-slate-300 p-3 text-sm font-mono resize-y focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none"
                  data-testid="email-textarea"
                />
              </div>

              {/* Upload buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { fileInputRef.current.accept = '.csv'; fileInputRef.current.click(); }}
                  data-testid="upload-csv-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Carica CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { fileInputRef.current.accept = '.txt'; fileInputRef.current.click(); }}
                  data-testid="upload-txt-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> Carica TXT
                </Button>
              </div>

              {/* Parsed emails preview */}
              {parsedEmails.length > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-700">
                      {parsedEmails.length} email riconosciute — {domains.length} domini
                    </span>
                    <button onClick={() => { setEmailText(''); setParsedEmails([]); }} className="text-xs text-slate-400 hover:text-slate-600">
                      Pulisci
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {parsedEmails.slice(0, 20).map((email, i) => (
                      <Badge key={i} variant="secondary" className="text-xs font-mono py-1">
                        {email}
                      </Badge>
                    ))}
                    {parsedEmails.length > 20 && (
                      <Badge variant="outline" className="text-xs">+{parsedEmails.length - 20} altre</Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Start button */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <span className="text-sm text-slate-500">
                  {parsedEmails.length > 0 ? `${domains.length} domini da analizzare — ~${Math.ceil(domains.length * 0.5)} min` : 'Inserisci email per iniziare'}
                </span>
                <Button
                  onClick={startLookup}
                  disabled={loading || parsedEmails.length === 0}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="start-lookup-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisi...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" /> Avvia Analisi</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Progress */}
        {job && job.status !== 'completed' && job.status !== 'failed' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="border-rose-200 bg-rose-50/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2 className="w-5 h-5 animate-spin text-rose-600" />
                  <span className="font-semibold text-rose-800">{statusLabel[job.status]}</span>
                </div>
                {job.current_task && <p className="text-sm text-rose-700 mb-3">{job.current_task}</p>}
                <div className="text-sm text-rose-700">
                  Domini analizzati: {job.processed || 0}/{job.total_emails ? Math.ceil(job.total_emails) : '?'}
                </div>
                <div className="mt-3 h-2 bg-rose-200 rounded-full overflow-hidden">
                  <div className="h-full bg-rose-600 rounded-full transition-all duration-500"
                    style={{ width: `${job.total_emails ? (job.processed || 0) / job.total_emails * 100 : 0}%` }} />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Results */}
        {job?.status === 'completed' && results.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

            {/* Actions bar */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900" data-testid="results-title">
                {results.length} aziende trovate — {results.reduce((sum, r) => sum + (r.referents?.length || 0), 0)} referenti
              </h2>
              <div className="flex gap-2">
                <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-2" /> Esporta CSV
                </Button>
                <Button onClick={saveAsLeads} data-testid="save-leads-btn">
                  <Save className="w-4 h-4 mr-2" /> Salva come Lead
                </Button>
              </div>
            </div>

            {/* Company cards */}
            {results.map((company, idx) => {
              const isExpanded = expandedDomain === idx;
              const network = company.linkedin_network || {};
              const totalReferents = company.referents?.length || 0;

              return (
                <Card key={idx} className="border-slate-200 overflow-hidden" data-testid={`company-card-${idx}`}>
                  {/* Company header */}
                  <button
                    onClick={() => setExpandedDomain(isExpanded ? null : idx)}
                    className="w-full text-left p-4 hover:bg-slate-50/50 transition"
                    data-testid={`expand-company-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {company.company_name || company.domain}
                          </h3>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {company.domain}</span>
                            {company.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {company.address}</span>}
                            {company.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {company.phone}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <Badge className="bg-rose-100 text-rose-700">
                          <Users className="w-3 h-3 mr-1" /> {totalReferents} referenti
                        </Badge>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t border-slate-100">
                      {/* Referents table */}
                      {totalReferents > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid={`referents-table-${idx}`}>
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs">Nome</th>
                                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs">Ruolo</th>
                                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs">Email</th>
                                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs">Stato</th>
                                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs">Fonte</th>
                                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs">Azioni</th>
                              </tr>
                            </thead>
                            <tbody>
                              {company.referents.map((ref, rIdx) => (
                                <tr key={rIdx} className="border-b border-slate-100 hover:bg-slate-50/50">
                                  <td className="py-2 px-4 font-medium text-slate-900 text-sm">
                                    {`${ref.first_name || ''} ${ref.last_name || ''}`.trim() || '-'}
                                  </td>
                                  <td className="py-2 px-4 text-slate-600 text-sm">{ref.position || '-'}</td>
                                  <td className="py-2 px-4">
                                    <span className="font-mono text-xs text-slate-700">{ref.email || '-'}</span>
                                  </td>
                                  <td className="py-2 px-4">
                                    {ref.verified ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                        <CheckCircle2 className="w-3 h-3 mr-1" /> Verificata
                                      </Badge>
                                    ) : ref.confidence ? (
                                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                                        <Shield className="w-3 h-3 mr-1" /> {ref.confidence}%
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-xs text-slate-400">Non verificata</Badge>
                                    )}
                                  </td>
                                  <td className="py-2 px-4">
                                    <Badge variant="outline" className="text-xs">
                                      {ref.source === 'hunter.io' ? 'Hunter' : 'Web'}
                                    </Badge>
                                  </td>
                                  <td className="py-2 px-4">
                                    <div className="flex gap-1">
                                      {ref.email && (
                                        <Button variant="ghost" size="sm" onClick={() => copyText(ref.email)} title="Copia email">
                                          <Copy className="w-3 h-3" />
                                        </Button>
                                      )}
                                      {ref.linkedin && (
                                        <a href={ref.linkedin} target="_blank" rel="noopener noreferrer">
                                          <Button variant="ghost" size="sm" title="LinkedIn"><Linkedin className="w-3 h-3" /></Button>
                                        </a>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Web emails + LinkedIn */}
                      <div className="p-4 space-y-3">
                        {company.web_emails?.length > 0 && (
                          <div>
                            <span className="text-xs font-semibold text-slate-500 block mb-1">Email dal sito web:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {company.web_emails.map((email, i) => (
                                <Badge key={i} variant="secondary" className="text-xs font-mono cursor-pointer" onClick={() => copyText(email)}>
                                  <Mail className="w-3 h-3 mr-1" /> {email}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {Object.keys(network).length > 0 && (
                          <div>
                            <span className="text-xs font-semibold text-slate-500 block mb-1">Rete LinkedIn:</span>
                            <div className="flex flex-wrap gap-2">
                              {network.company_search && (
                                <a href={network.company_search} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-7">
                                    <Building2 className="w-3 h-3 mr-1" /> Azienda <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                  </Button>
                                </a>
                              )}
                              {network.people_at_company && (
                                <a href={network.people_at_company} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-7">
                                    <Users className="w-3 h-3 mr-1" /> Dipendenti <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                  </Button>
                                </a>
                              )}
                              {network.ex_employees && (
                                <a href={network.ex_employees} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-100 text-xs h-7">
                                    <Users className="w-3 h-3 mr-1" /> Ex-Dipendenti <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                  </Button>
                                </a>
                              )}
                              {network.similar_companies && (
                                <a href={network.similar_companies} target="_blank" rel="noopener noreferrer">
                                  <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-xs h-7">
                                    <Building2 className="w-3 h-3 mr-1" /> Simili <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                  </Button>
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </motion.div>
        )}

        {/* No results */}
        {job?.status === 'completed' && results.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">Nessun risultato</h3>
              <p className="text-slate-500">Non sono stati trovati dati per le email inserite</p>
            </CardContent>
          </Card>
        )}

        {job?.status === 'failed' && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <p className="text-red-700 font-medium">Errore: {job.error || 'Analisi fallita'}</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
